import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  type CreateUserInput,
  type CreateUserMutation,
  type CreateUserMutationVariables,
  type CreateUserWithProgramRoleInput,
  type CreateUserWithProgramRoleMutation,
  type CreateUserWithProgramRoleMutationVariables,
  type DestroyUserMutationMutation,
  type DestroyUserMutationMutationVariables,
  type GenerateUserPushNotificationsExternalIdMutation,
  type GenerateUserPushNotificationsExternalIdMutationVariables,
  type RequestPasswordResetMutation,
  type RequestPasswordResetMutationVariables,
  type ResetPasswordMutation as ResetPasswordMutationMutation,
  type ResetPasswordMutationVariables,
  type SendWelcomeMailMutation,
  type SendWelcomeMailMutationVariables,
  type UnsubscribeToLearningResourceAlertMailsMutation,
  type UnsubscribeToLearningResourceAlertMailsMutationVariables,
  type UpdateProtectedUserFieldsInput,
  type UpdateProtectedUserFieldsMutation,
  type UpdateProtectedUserFieldsMutationVariables,
  type User,
  type UserAvatarQueryQuery,
  type UserAvatarQueryQueryVariables,
  type UserByEmailQueryQuery,
  type UserByEmailQueryQueryVariables,
  type WatchUserWithCustomSettingsQuery,
  type WatchUserWithCustomSettingsQueryVariables,
  type WatchUserWithSecondFactorQueryQuery,
  type WatchUserWithSecondFactorQueryQueryVariables,
  WelcomeMailSourceEnum,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { type UpdateBasicUserFieldsInput } from '../../schema/schema-mutation-input';
import { SharedGraphQlService } from '../../services/shared-graphql.service';
import { invalidateFieldsFromObject } from '../../utils/cache-invalidator';
import { assertNotNull } from '../../utils/nullable.utils';
import { typeOrNull } from '../../utils/type-helpers';
import { userMinimalFragment } from '../minimal.fragments';
import { userItemFragment } from './shared-user.fragments';
import { ResetPasswordMutation, UpdateBasicUserFieldsMutation } from './shared-user.mutations';
import { WatchUserWithSecondFactorQuery } from './shared-user.queries';

@Injectable({
  providedIn: 'root',
})
export class SharedUserApi {
  private sharedGraphQlService = inject(SharedGraphQlService);

  private translateService = inject(TranslateService);

  createUserWithProgramRole(
    createUserWithProgramRoleInput: CreateUserWithProgramRoleInput,
    options?: {
      invalidates: { gid: string; fields: string[] }[];
    },
  ) {
    return this.sharedGraphQlService
      .mutateV2<CreateUserWithProgramRoleMutation, CreateUserWithProgramRoleMutationVariables>({
        mutation: gql`
          mutation createUserWithProgramRole($input: CreateUserWithProgramRoleInput!) {
            createUserWithProgramRole(input: $input) {
              user {
                ...UserItem
              }

              errors {
                message
                path
              }
            }
          }
          ${userItemFragment}
        `,
        variables: {
          input: createUserWithProgramRoleInput,
        },
        update: invalidateFieldsFromObject(options?.invalidates ?? []),
      })
      .pipe(
        catchError((e) => {
          throw e;
        }),
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        map((result) => result.data.createUserWithProgramRole.user),
      );
  }

  createUser(
    createUserInput: CreateUserInput,
    options?: {
      invalidates: { gid: string; fields: string[] }[];
    },
  ) {
    return this.sharedGraphQlService
      .mutateV2<CreateUserMutation, CreateUserMutationVariables>({
        mutation: gql`
          mutation createUser($input: CreateUserInput!) {
            createUser(input: $input) {
              user {
                ...UserItem
              }

              errors {
                message
                path
              }
            }
          }
          ${userItemFragment}
        `,
        variables: {
          input: createUserInput,
        },
        update: invalidateFieldsFromObject(options?.invalidates ?? []),
      })
      .pipe(
        catchError((e) => {
          throw e;
        }),
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        map((result) => result.data.createUser.user),
      );
  }

  updateUser(updateUserInput: UpdateBasicUserFieldsInput): Observable<User> {
    return this.sharedGraphQlService
      .mutate({
        mutation: UpdateBasicUserFieldsMutation,
        variables: {
          input: updateUserInput,
        },
      })
      .pipe(
        map((result) => {
          // @ts-expect-error TS18049 possibly null or undefined
          return result.data.updateBasicUserFields.user;
        }),
      );
  }

  requestPasswordReset(email: string, redirectSource: string) {
    return (
      this.sharedGraphQlService
        .mutateV2<RequestPasswordResetMutation, RequestPasswordResetMutationVariables>({
          mutation: gql`
            mutation requestPasswordReset($input: RequestPasswordResetInput!) {
              requestPasswordReset(input: $input) {
                email

                clientMutationId
                errors {
                  message
                  path
                }
              }
            }
          `,
          variables: {
            input: {
              email: email,
              source: redirectSource,
            },
          },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.requestPasswordReset))
    );
  }

  updateUserProtectedFields(updateUserInput: UpdateProtectedUserFieldsInput) {
    return this.sharedGraphQlService
      .mutateV2<UpdateProtectedUserFieldsMutation, UpdateProtectedUserFieldsMutationVariables>({
        mutation: gql`
          mutation UpdateProtectedUserFields($input: UpdateProtectedUserFieldsInput!) {
            updateProtectedUserFields(input: $input) {
              user {
                ...UserItem
              }

              errors {
                message
                path
              }
            }
          }
          ${userItemFragment}
        `,
        variables: {
          input: updateUserInput,
        },
      })
      .pipe(
        map((result) => {
          // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
          return result.data.updateProtectedUserFields.user;
        }),
        catchError((error) => {
          if (error.message.includes('Current password incorrect')) {
            return throwError(() => ({
              passwordError: this.translateService.instant('qz-password-incorrect-zm'),
            }));
          }

          const emailError = 'Email is al in gebruik'; // DISABLE_TRANSLATION_CHECK
          if (error.message.includes(emailError)) {
            return throwError(() => ({
              emailError: this.translateService.instant('qz-chosen-email-address-already-in-use-zm'),
            }));
          }

          return throwError(() => error);
        }),
      );
  }

  deleteUser(userId: string) {
    return this.sharedGraphQlService
      .mutateV2<DestroyUserMutationMutation, DestroyUserMutationMutationVariables>({
        mutation: gql`
          mutation DestroyUserMutation($input: DestroyUserInput!) {
            destroyUser(input: $input) {
              clientMutationId
              user {
                id
                fullName
              }
              errors {
                message
                path
              }
            }
          }
        `,
        variables: {
          input: {
            userId: userId,
          },
        },
      })
      .pipe(
        map((result) => {
          // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
          return result.data.destroyUser.user;
        }),
      );
  }

  watchAvatar(id: string) {
    return this.sharedGraphQlService
      .watchQuery<UserAvatarQueryQuery, UserAvatarQueryQueryVariables>({
        query: gql`
          query UserAvatarQuery($id: ID!) {
            node(id: $id) {
              ... on User {
                ...UserMinimal
                fullSizeAvatarUrl
                avatarUrl
                thumbUrl
              }
            }
          }
          ${userMinimalFragment}
        `,
        variables: {
          id,
        },
      })
      .pipe(map((result) => assertNotNull(typeOrNull(assertNotNull(result.data.node), 'User'))));
  }

  watchUserWithSecondFactor(userId: string) {
    return this.sharedGraphQlService
      .watchQuery<WatchUserWithSecondFactorQueryQuery, WatchUserWithSecondFactorQueryQueryVariables>({
        query: WatchUserWithSecondFactorQuery,
        variables: {
          id: userId,
        },
      })
      .pipe(map((result) => assertNotNull(typeOrNull(result.data.node, 'User'))));
  }

  resetPassword(token: string) {
    return (
      this.sharedGraphQlService
        .mutateV2<ResetPasswordMutationMutation, ResetPasswordMutationVariables>({
          mutation: ResetPasswordMutation,
          variables: {
            input: {
              token: token,
              password: '',
              passwordConfirmation: '',
            },
          },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.resetPassword))
    );
  }

  updatePassword(token: string, password: string, passwordConfirmation: string) {
    return (
      this.sharedGraphQlService
        .mutateV2<ResetPasswordMutationMutation, ResetPasswordMutationVariables>({
          mutation: ResetPasswordMutation,
          variables: {
            input: {
              token: token,
              password: password,
              passwordConfirmation: passwordConfirmation,
            },
          },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.resetPassword))
    );
  }

  /**
   * @param email
   * @param source: default RedirectSourceEnum.PORTFOLIO
   *
   * @return the email where the mail was sent to
   */
  sendWelcomeEmailToEmailAddress(email: string, source: WelcomeMailSourceEnum = WelcomeMailSourceEnum.PORTFOLIO) {
    return (
      this.sharedGraphQlService
        .mutateV2<SendWelcomeMailMutation, SendWelcomeMailMutationVariables>({
          mutation: gql`
            mutation SendWelcomeMail($input: SendWelcomeMailInput!) {
              sendWelcomeMail(input: $input) {
                email

                clientMutationId
                errors {
                  message
                  path
                }
              }
            }
          `,
          variables: { input: { email, source } },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.sendWelcomeMail.email))
    );
  }

  watchViewerCustomSettings(): Observable<Pick<User, 'id' | 'customSettings'>> {
    // @ts-expect-error TS2322 type not assignable
    return this.sharedGraphQlService
      .watchQuery<WatchUserWithCustomSettingsQuery, WatchUserWithCustomSettingsQueryVariables>({
        query: gql`
          query WatchUserWithCustomSettings {
            viewer {
              id
              customSettings
            }
          }
        `,
      })
      .pipe(map((result) => result.data.viewer));
  }

  findUserByEmail(email: string) {
    return this.sharedGraphQlService
      .watchQuery<UserByEmailQueryQuery, UserByEmailQueryQueryVariables>({
        query: gql`
          query UserByEmailQuery($email: String!) {
            userByEmail(email: $email) {
              ...UserMinimal
              email
              isTrusted
              hasValidPin
              isOnboarded
              big
            }
          }
          ${userMinimalFragment}
        `,
        variables: {
          email,
        },
        fetchPolicy: 'network-only', // always use network to search user
      })
      .pipe(
        take(1),
        map((result) => result.data.userByEmail),
      );
  }

  unsubscribeToLearningResourceAlertMails(token: string) {
    return (
      this.sharedGraphQlService
        .mutateV2<
          UnsubscribeToLearningResourceAlertMailsMutation,
          UnsubscribeToLearningResourceAlertMailsMutationVariables
        >({
          mutation: gql`
            mutation UnsubscribeToLearningResourceAlertMails($input: UnsubscribeToLearningResourceAlertMailsInput!) {
              unsubscribeToLearningResourceAlertMails(input: $input) {
                errors {
                  message
                  path
                }
              }
            }
          `,
          variables: { input: { token } },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.unsubscribeToLearningResourceAlertMails))
    );
  }

  generateUserPushNotificationsExternalId(userId: string) {
    return (
      this.sharedGraphQlService
        .mutateV2<
          GenerateUserPushNotificationsExternalIdMutation,
          GenerateUserPushNotificationsExternalIdMutationVariables
        >({
          mutation: gql`
            mutation GenerateUserPushNotificationsExternalId($input: GenerateUserPushNotificationsExternalIdInput!) {
              generateUserPushNotificationsExternalId(input: $input) {
                user {
                  id
                  pushNotificationsExternalId
                }
                errors {
                  message
                  path
                }
              }
            }
          `,
          variables: {
            input: { userId },
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.generateUserPushNotificationsExternalId.user))
    );
  }
}
